import type { SerializedStyles } from '@emotion/react';

import type * as React from 'react';

import type { ButtonProps } from '@coursera/cds-core';
import type {
  CarouselFilter as CarouselFilterTrackingData,
  PageConfigComponent as PageConfigComponentTrackingData,
} from '@coursera/event-pulse-types';

import type { ModuleLayoutConfig } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import type { ImageType } from 'bundles/cds-labs/components/Modules/components/Image';
import type { GridConfig } from 'bundles/cds-labs/components/ShowMoreGridSection/ShowMoreGridSection';
import type { CarouselCollection } from 'bundles/collections-carousel/types/CarouselCollection';

export type CTA = {
  text?: string | null;
  target?: string | null;
  variant?: ButtonProps['variant'] | null;
  size?: ButtonProps['size'];
  showCtaArrowIcon?: boolean;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
};

export const COLLECTIONS_MODULE_DISPLAY_STYLE = {
  SPOTLIGHT: 'spotlight',
  DEFAULT: 'default',
} as const;

export const headerOrder = ['h2', 'h3', 'h4', 'h5', 'h6'] as const;

export type HeaderLevel = (typeof headerOrder)[number];

export type Filter = {
  id: string;
  filterName: string;
  filterImage?: ImageType | null;
  eventingData?: {
    carouselFilter?: CarouselFilterTrackingData;
  };
};

type EventingData = {
  searchQuery?: string;
  page?: string;
  filtersApplied?: Record<string, string[]>;
  searchIndex?: string;
  searchIndexPosition?: number;
  pageConfigComponent?: PageConfigComponentTrackingData;
};

/** Props shared by both CollectionsModule and SpotlightCollectionsModule. */
export type BaseCollectionsModuleProps = {
  headline?: string | null;
  headlineComponent?: HeaderLevel;
  description?: React.ReactNode;
  filters?: Filter[] | null;
  collections?: CarouselCollection[];
  isCollectionsLoading?: boolean;
  onFilterChange?: (index: number, filterName: string) => void;
  currentCollectionSetIndex?: number;
  numOfEntriesPerCollection?: number;
  cta?: CTA;
  customCtaButton?: React.ReactNode;
  customChipStyles?: SerializedStyles;
  productCardTrackingName?: string | null;
  alwaysShowChipFilters?: boolean;
  eventingData?: EventingData;
  customHeadline?: JSX.Element;
  eyebrow?: {
    image?: ImageType;
  };
  customBackgroundColor?: string | null;
  textColorOverride?: string | null;
  dataTestId?: string;
} & ModuleLayoutConfig;

/** Props for SpotlightCollectionsModule (no non-spotlight props like showAllEntries, customItemsGridConfig). */
export type SpotlightCollectionsModuleProps = BaseCollectionsModuleProps & {
  /** When true, renders the CTA button on all screen sizes instead of making the headline a link on mobile/tablet. */
  renderPrimaryCtaOnMobile?: boolean | null;
};

/** Full props for CollectionsModule (supports both display styles). */
export type CollectionsModuleProps = BaseCollectionsModuleProps & {
  showAllEntries?: boolean | null;
  customItemsGridConfig?: GridConfig;
  displayStyle?: string | null;
  renderPrimaryCtaOnMobile?: boolean | null;
  /** Force the list-view card variant regardless of viewport. Passed through
   * to CollectionsContainer → ProductCardCollection. */
  forceListView?: boolean;
};
